import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Instant } from "@js-joda/core";
import { auth } from "../../../core/auth";
import {
  ApplyEligibility,
  ApplyResponse,
  applyBlockerTone,
  formatHours,
  formatTimestamp,
  text,
} from "../ai-weekly-template-builder.utils";

interface Props {
  /** The combined verdict for the suggestion on screen, from the server. */
  eligibility: ApplyEligibility;
  appliedAt: Instant | null;
  appliedByName: string | null;
  isApplying: boolean;
  /** Set only after a successful apply of the suggestion currently shown. */
  result: ApplyResponse | null;
  onClickApply: () => void;
}

/**
 * The action, and the reason it is unavailable when it is.
 *
 * Presentational: every decision comes from the server's `eligibility`, never
 * re-derived here, so the button and the endpoint cannot disagree.
 *
 * Reasons are shown as inline text or an alert rather than a tooltip. A disabled
 * Chakra button renders a native `disabled` attribute and Chrome does not
 * dispatch mouse events on those, so a tooltip is unreliable — and "not
 * available for New York yet, add the template manually" is an instruction, not a
 * hint to be discovered by hovering.
 */
export default function AiWeeklyTemplateApplyBar(props: Props) {
  // Just applied, in this session: show what happened rather than the button.
  if (props.result !== null) {
    return <AppliedResult result={props.result} />;
  }

  const alreadyApplied = props.appliedAt !== null;
  if (alreadyApplied) {
    return (
      <Alert status="success">
        <AlertIcon />
        <AlertDescription>
          Applied
          {props.appliedByName !== null && ` by ${props.appliedByName}`}
          {props.appliedAt !== null && ` on ${formatTimestamp(props.appliedAt)}`}. The weekly
          template below was created from this suggestion.
        </AlertDescription>
      </Alert>
    );
  }

  // Nothing applyable exists (an INVALID or MATH_MISMATCH result, or an empty
  // schedule). The status alert at the top of the panel already explains it, so
  // showing a dead button here would only invite "why not?".
  const hasNothingToApply = props.eligibility.blockers.some(
    (blocker) =>
      blocker.code === "SUGGESTION_STATUS_NOT_APPLYABLE" ||
      blocker.code === "SUGGESTION_HAS_NO_SHIFTS"
  );
  if (hasNothingToApply) {
    return null;
  }

  // A coordinator who cannot create visits should not be shown a disabled
  // button for something they will never be able to do.
  if (!auth.isAllowedTo("create_new_patient_visit")) {
    return null;
  }

  const infoBlockers = props.eligibility.blockers.filter(
    (blocker) => applyBlockerTone(blocker) === "info"
  );
  const mutedBlockers = props.eligibility.blockers.filter(
    (blocker) => applyBlockerTone(blocker) === "muted"
  );

  return (
    <Box borderRadius="md" borderWidth="1px" p={3}>
      <Flex alignItems="center" gap={4} justifyContent="space-between">
        <Box>
          <Text fontWeight="semibold">Create weekly template &amp; visits</Text>
          <Text color="gray.600" fontSize="sm">
            Writes this repeating week to the patient&apos;s weekly template and creates the visits
            for it.
          </Text>
        </Box>
        <Button
          colorScheme="blue"
          flexShrink={0}
          isDisabled={!props.eligibility.canApply || props.isApplying}
          isLoading={props.isApplying}
          loadingText="Creating…"
          onClick={props.onClickApply}
        >
          Create
        </Button>
      </Flex>

      {infoBlockers.map((blocker) => (
        <Alert key={blocker.code} fontSize="sm" mt={3} status="info">
          <AlertIcon />
          <AlertDescription>{blocker.message}</AlertDescription>
        </Alert>
      ))}

      {mutedBlockers.map((blocker) => (
        <Text key={blocker.code} color="gray.600" fontSize="sm" mt={2}>
          {blocker.message}
        </Text>
      ))}
    </Box>
  );
}

function AppliedResult(props: { result: ApplyResponse }) {
  const { result } = props;
  const failedTrueUps = result.trueUps.filter((trueUp) => !trueUp.created);
  const hasCaveats = result.warnings.length > 0;

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Alert status={hasCaveats ? "warning" : "success"}>
        <AlertIcon />
        <AlertDescription>
          Created {result.templateShiftsCreated} weekly template shift
          {result.templateShiftsCreated === 1 ? "" : "s"}, {result.recurringVisitsCreated} recurring
          visit
          {result.recurringVisitsCreated === 1 ? "" : "s"}
          {result.trueUpVisitsCreated > 0 && ` and ${result.trueUpVisitsCreated} top-up visit`}
          {result.trueUpVisitsCreated > 1 ? "s" : ""}
          {result.firstVisitDate !== null &&
            result.lastVisitDate !== null &&
            `, from ${text(result.firstVisitDate)} to ${text(result.lastVisitDate)}`}
          .
        </AlertDescription>
      </Alert>

      {result.warnings.map((warning) => (
        <Alert key={warning.code} fontSize="sm" status="warning">
          <AlertIcon />
          <AlertDescription>{warning.message}</AlertDescription>
        </Alert>
      ))}

      {failedTrueUps.length > 0 && (
        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb={1}>
            Add these by hand
          </Text>
          {/*
            A plain Box, not a direct flex child of ModalBody: an overflowX there
            collapses to zero height. Inside the panel's own Box it is safe, the
            same as the panel's other tables.
          */}
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th isNumeric>Length</Th>
                  <Th>Reason</Th>
                </Tr>
              </Thead>
              <Tbody>
                {failedTrueUps.map((trueUp, index) => (
                  <Tr key={`${text(trueUp.date)}-${index}`}>
                    <Td>{text(trueUp.date)}</Td>
                    <Td isNumeric>{formatHours(trueUp.additionalMinutes)}</Td>
                    <Td>{trueUp.failureReason}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}
    </Box>
  );
}
